import { callGeotabAPI } from '../api/geotab-client';
import {
  splitDateRange, mapLimit, fetchAdaptiveWindow, MAX_CONCURRENT, FLEET_WIDE_TRIP_LIMIT,
} from '../api/fetch-range';
import { state } from '../state';
import type { Trip } from '../api/types';
import type { AssetVehicleRow } from './assets-types';
import { parseTimespanToHrs } from '../utils/time';
import {
  getSelectedGroups,
  setAssetsRawRows,
  assetsSortCol,
  assetsSortAsc,
} from './assets-page';
import { renderAssetsResults, showAssetsState } from './assets-results';

// ── Run analysis ──────────────────────────────────────────────────────────

export async function runAssetsAnalysis(): Promise<void> {
  const fromVal = (document.getElementById('auFrom') as HTMLInputElement).value;
  const toVal   = (document.getElementById('auTo')   as HTMLInputElement).value;
  if (!fromVal || !toVal) { alert('Please select a date range.'); return; }

  const from = new Date(fromVal + 'T00:00:00');
  const to   = new Date(toVal   + 'T23:59:59');
  if (from >= to) { alert('Start date must be before end date.'); return; }

  const selectedGroups = getSelectedGroups();
  if (!selectedGroups.size) { alert('Select at least one group.'); return; }

  const runBtn = document.getElementById('auRunBtn') as HTMLButtonElement;
  runBtn.disabled  = true;
  runBtn.innerHTML = '<span style="opacity:0.6">Loading…</span>';

  showAssetsState('loading', 'Fetching trip data…');

  try {
    // Aggregate incrementally: fetch the range in windows, fold each window's trips into per-
    // vehicle accumulators, then discard it. We never hold the whole trip set — this fleet tops
    // 25k trips (Geotab's real per-call cap for a fleet-wide Trip.Get — see FLEET_WIDE_TRIP_LIMIT)
    // even in a week, so loading all of them (30d/YTD) onto the main thread would freeze the page.
    // A window that still hits the cap is split until complete; trips are de-duped by id across
    // the shared window boundaries.
    interface Acc {
      days: Set<string>; driveHrs: number; idleHrs: number;
      distKm: number; trips: number; lastStop: string | null;
    }
    const accByDevice = new Map<string, Acc>();
    const getAcc = (id: string): Acc => {
      let a = accByDevice.get(id);
      if (!a) {
        a = { days: new Set(), driveHrs: 0, idleHrs: 0, distKm: 0, trips: 0, lastStop: null };
        accByDevice.set(id, a);
      }
      return a;
    };

    const seenTrips = new Set<string>();
    let tripTotal = 0;
    let capHit = false;

    await mapLimit(splitDateRange(from.toISOString(), to.toISOString(), 7), MAX_CONCURRENT,
      async ([wFrom, wTo]) => {
        const trips = await fetchAdaptiveWindow<Trip>(
          wFrom, wTo, FLEET_WIDE_TRIP_LIMIT,
          (f, t) => callGeotabAPI<Trip[]>('Get', {
            typeName: 'Trip', search: { fromDate: f, toDate: t }, resultsLimit: FLEET_WIDE_TRIP_LIMIT,
          }),
          { onCap: () => { capHit = true; } },
        );
        for (const trip of trips) {
          const id = trip.device?.id;
          if (!id) continue;
          if (trip.id) { if (seenTrips.has(trip.id)) continue; seenTrips.add(trip.id); }
          const a = getAcc(id);
          if (trip.start) a.days.add(trip.start.slice(0, 10));
          a.driveHrs += parseTimespanToHrs(trip.drivingDuration);
          a.idleHrs  += parseTimespanToHrs(trip.idlingDuration);
          a.distKm   += trip.distance ?? 0;
          a.trips    += 1;
          if (trip.stop && (!a.lastStop || trip.stop > a.lastStop)) a.lastStop = trip.stop;
          tripTotal++;
        }
        // `trips` falls out of scope here and is freed before the next window.
      });

    const warnBanner = document.getElementById('auWarnBanner')!;
    if (capHit) {
      warnBanner.textContent =
        `⚠ A single hour exceeded ${FLEET_WIDE_TRIP_LIMIT.toLocaleString()} trips fleet-wide — that window may be incomplete.`;
      warnBanner.classList.remove('hidden');
    } else {
      warnBanner.classList.add('hidden');
    }

    // Total calendar days in range (inclusive: both start and end date count)
    const totalDays = Math.max(
      1,
      Math.floor((new Date(toVal + 'T00:00:00').getTime() - from.getTime()) / 86400000) + 1
    );

    // Build one row per active vehicle (filtered by selected groups)
    const rows: AssetVehicleRow[] = [];

    for (const v of state.allVehicles) {
      const group = v.group || 'No Group';
      if (!selectedGroups.has(group)) continue;

      const a = accByDevice.get(v.device.id);
      const activeDays      = a ? a.days.size : 0;
      const totalDriveHrs   = a ? a.driveHrs  : 0;
      const totalIdleHrs    = a ? a.idleHrs   : 0;
      const totalDistanceKm = a ? a.distKm    : 0;
      const tripCount       = a ? a.trips     : 0;
      const lastTripDate    = a ? a.lastStop  : null;

      const dayUtilPct    = totalDays > 0 ? (activeDays / totalDays) * 100 : 0;
      const totalEngineHrs = totalDriveHrs + totalIdleHrs;
      const availableHrs  = totalDays * 24;
      const hrUtilPct     = availableHrs > 0
        ? Math.min((totalEngineHrs / availableHrs) * 100, 100)
        : 0;
      const avgTripKm     = tripCount > 0 ? totalDistanceKm / tripCount : 0;

      rows.push({
        deviceId: v.device.id,
        name:     v.device.name,
        group,
        tripCount,
        activeDays,
        totalDays,
        dayUtilPct,
        totalEngineHrs,
        totalDriveHrs,
        totalIdleHrs,
        availableHrs,
        hrUtilPct,
        totalDistanceKm,
        avgTripKm,
        lastTripDate,
      });
    }

    setAssetsRawRows(rows);

    // Meta line
    const metaEl = document.getElementById('auMeta');
    if (metaEl) {
      metaEl.textContent =
        from.toLocaleDateString() + ' – ' + to.toLocaleDateString() +
        ' · ' + rows.length + ' vehicles · ' +
        tripTotal.toLocaleString() + ' trips · ' +
        totalDays + ' day window · 24h/day';
    }

    renderAssetsResults(rows, assetsSortCol, assetsSortAsc);
    showAssetsState('results');

    const exportBtn = document.getElementById('auExportBtn') as HTMLElement;
    exportBtn.style.display = '';

  } catch (err) {
    showAssetsState('error', 'Error: ' + (err as Error).message);
  } finally {
    runBtn.disabled  = false;
    runBtn.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><polygon points="3,1 13,7 3,13" fill="currentColor"/></svg> Run Analysis';
  }
}
